import { useRequiresInitialDeposit } from 'client/hooks/subaccount/useRequiresInitialDeposit';
import { useIsConnected } from 'client/hooks/util/useIsConnected';
import { useIsPrivyEmbeddedWallet } from 'client/hooks/util/useIsPrivyEmbeddedWallet';
import { useIsSmartContractWalletConnected } from 'client/hooks/util/useIsSmartContractWalletConnected';
import { SMART_CONTRACT_WALLET_HELPER_TOAST_ID } from 'client/modules/notifications/handlers/handleSmartContractWalletHelperNotificationDispatch';
import { useNotificationManagerContext } from 'client/modules/notifications/NotificationManagerContext';
import { useIsSingleSignatureSession } from 'client/modules/singleSignatureSessions/hooks/useIsSingleSignatureSession';
import { shouldShowSmartContractWallet1CTPrompt } from 'client/modules/singleSignatureSessions/utils/smartContractWallet1ctUx';
import { useEffect } from 'react';
import { toast } from 'react-hot-toast';

interface SmartContractWalletHelperState {
  isConnected: boolean;
  isPrivyEmbeddedWallet: boolean;
  isSingleSignatureSession: boolean;
  isSmartContractWallet: boolean;
  requiresFirstDeposit: boolean;
}

export function shouldShowSmartContractWalletHelper({
  isConnected,
  isPrivyEmbeddedWallet,
  isSingleSignatureSession,
  isSmartContractWallet,
  requiresFirstDeposit,
}: SmartContractWalletHelperState) {
  return (
    shouldShowSmartContractWallet1CTPrompt({
      isPrivyEmbeddedWallet,
      isSingleSignatureSession,
      isSmartContractWallet,
    }) &&
    isConnected &&
    !requiresFirstDeposit
  );
}

export function SmartContractWalletHelperEventEmitter() {
  const { dispatchNotification } = useNotificationManagerContext();
  const isSingleSignatureSession = useIsSingleSignatureSession({
    requireActive: true,
  });
  const isSmartContractWallet = useIsSmartContractWalletConnected();
  const isPrivyEmbeddedWallet = useIsPrivyEmbeddedWallet();
  const isConnected = useIsConnected();
  const requiresFirstDeposit = useRequiresInitialDeposit();

  // Show notification if user is connected to a smart contract wallet and not in a single signature session
  // We also need to check if the user is connected to the app so we don't prematurely show the notification
  // Lastly only show the notification if the user has already deposited funds
  const shouldShow = shouldShowSmartContractWalletHelper({
    isConnected,
    isPrivyEmbeddedWallet,
    isSingleSignatureSession,
    isSmartContractWallet: !!isSmartContractWallet,
    requiresFirstDeposit,
  });

  useEffect(() => {
    if (shouldShow) {
      dispatchNotification({ type: 'smart_contract_wallet_helper' });
    } else {
      toast.dismiss(SMART_CONTRACT_WALLET_HELPER_TOAST_ID);
    }
  }, [dispatchNotification, shouldShow]);

  return null;
}
